import React, { useEffect, useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import type { Order } from '../types'
import { Link } from 'react-router-dom'
import { getUnreadCount } from '../lib/chatService'

const statusLabels: Record<Order['status'], string> = {
  EN_ATTENTE: 'En attente',
  ACCEPTEE: 'Acceptée',
  REFUSEE: 'Refusée',
  EN_COURS: 'En cours',
  PRET_A_RECUPERER: 'Prête à récupérer',
  EN_LIVRAISON: 'En livraison',
  LIVREE: 'Livrée',
  CLOTUREE: 'Clôturée',
  ANNULEE: 'Annulée'
}


const MyOrdersPage: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([])
  const [loading, setLoading] = useState(true)
  const [unreadLoading, setUnreadLoading] = useState(true)


  useEffect(() => {
    const load = async () => {
      const { data, error } = await supabase
        .from('orders')
        .select('id, status, total_price, created_at, payment_status')
        .order('created_at', { ascending: false })

       if (error) {
        console.error(error)
        setLoading(false)
        return
      }

      const baseOrders = data ?? []

      if (baseOrders.length === 0) {
        setOrders([])
        setLoading(false)
        return
      }
    // 🔔 Récupération des messages non lus (envoyés par l'ADMIN)
      try {
        const unreadMap = await getUnreadCount(baseOrders.map(o => o.id), 'USER')
        const enriched = baseOrders.map(o => ({
          ...o,
          unread_count: unreadMap[o.id] ?? 0
        }))
        setOrders(enriched)
      } catch (err) {
        console.error('Erreur lors du chargement des messages non lus:', err)
        // Fallback : afficher les commandes sans les compteurs
        setOrders(baseOrders.map(o => ({ ...o, unread_count: 0 })))
      }
      setLoading(false)
    }

    load()
  }, [])

  if (loading) {
    return <p>Chargement de tes commandes…</p>
  }

  if (orders.length === 0) {
    return (
      <div className="card">
        <h1 className="card-title">Mes commandes</h1>
        <p style={{ fontSize: '0.85rem', color: '#9ca3af' }}>
          Tu n&apos;as encore aucun service en cours de réalisation.
        </p>
        <Link to="/order/start" className="btn btn-primary" style={{ marginTop: '0.75rem' }}>
          Commander un service
        </Link>
      </div>
    )
  }

  return (
    <div className="card">
      <div className="card-header">
        <h1 className="card-title">Mes commandes</h1>
        <span className="badge">{orders.length} commande(s)</span>
      </div>

      <div>
        {orders.map(order => (
          <Link
            key={order.id}
            to={`confirmation?orderId=${order.id}`}
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            <div
              style={{
                padding: '0.75rem 0',
                borderBottom: '1px solid rgba(148,163,184,0.3)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer'
              }}
            >
              <div>
                <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                  Commande #{order.id.slice(0, 8)}
                </div>
                <div style={{ fontSize: '0.8rem', color: '#9ca3af' }}>
                  Créée le {new Date(order.created_at).toLocaleString('fr-CH')}
                </div>
              </div>

              <div style={{ textAlign: 'right' }}>
                <div className="chip">
                  <span className="chip-dot" />
                  {statusLabels[order.status]}
                </div>
                <div
                  style={{
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    marginTop: '0.25rem'
                  }}
                >
                  {order.total_price.toFixed(2)} CHF
                </div>
                 {/* 💬 Badge messages non lus */}
                {order.unread_count && order.unread_count > 0 && (
                  <div
                    style={{
                      marginTop: '.25rem',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '.25rem',
                      background: '#22c55e',
                      color: '#020617',
                      fontSize: '.7rem',
                      fontWeight: 700,
                      padding: '0.15rem 0.5rem',
                      borderRadius: '999px'
                    }}
                  >
                    💬 {order.unread_count}
                  </div>
                )}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  )
}

export default MyOrdersPage
